import { api } from './authService';

// Types based on the documentation
export interface TransferRecipientData {
  id: string;
  name: string;
  identifier: string;
  issuer_name: string;
  issuer_type: string;
  country: string;
  currency: string;
  user_id: string;
  profile_id: string;
  supports_only_integer_amount: boolean;
  additional_info?: {
    beneficiary_address?: string;
    bic_swift_code?: string;
    bank_address?: string;
    sort_code?: string;
    ach_routing_number?: string;
    wire_routing_number?: string;
    account_type?: string;
    postal_code?: string;
    street_number?: string;
    street_name?: string;
    city?: string;
    routing_number?: string;
    email?: string;
    issuer_branch_code?: string;
    issuer_branch_name?: string;
    beneficiary_country?: string;
    beneficiary_state?: string;
    beneficiary_mobile_number?: string;
  };
  created: string;
}

export interface TransferSchema {
  id: string;
  reference: string;
  transaction_id: string;
  amount: number;
  currency: string;
  status: string;
  fee: object;
  recipient_id: string;
  recipient_name: string;
  recipient_identifier: string;
  recipient_bank: string;
  recipient_bank_code: string;
  issuer_type: string;
  purpose: string;
  user_id: string;
  profile_id: string;
  created: number;
  transfer_recipient?: TransferRecipientData;
}

export interface GetTransfersParams {
  currency?: string;
  reference?: string;
  transaction_id?: string;
  status?: string;
  issuer_type?: string;
  purpose?: string;
  provider_id?: string;
  user_id?: string;
  profile_id?: string;
  from?: number;
  to?: number;
  next_cursor?: string;
}

export interface GetTransfersResponse {
  next_cursor?: string;
  transfers: TransferSchema[];
}

export interface OfflinePayoutRequest {
  amount_sent: number;
  proof_of_payout_url: string;
}

// Get transfers with optional filters
export const getTransfers = async (params?: GetTransfersParams): Promise<GetTransfersResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params?.currency) queryParams.append('currency', params.currency);
    if (params?.reference) queryParams.append('reference', params.reference);
    if (params?.transaction_id) queryParams.append('transaction_id', params.transaction_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.issuer_type) queryParams.append('issuer_type', params.issuer_type);
    if (params?.purpose) queryParams.append('purpose', params.purpose);
    if (params?.provider_id) queryParams.append('provider_id', params.provider_id);
    if (params?.user_id) queryParams.append('user_id', params.user_id);
    if (params?.profile_id) queryParams.append('profile_id', params.profile_id);
    if (params?.from) queryParams.append('from', params.from.toString());
    if (params?.to) queryParams.append('to', params.to.toString());
    if (params?.next_cursor) queryParams.append('next_cursor', params.next_cursor);

    const url = `/admin/transfers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<GetTransfersResponse>(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching transfers:', error);
    throw error;
  }
};

// Get single transfer with recipient details
export const getTransfer = async (id: string, includeRecipient = false): Promise<TransferSchema> => {
  try {
    const queryParams = new URLSearchParams();
    if (includeRecipient) {
      queryParams.append('include_transfer_recipient', '1');
    }
    
    const url = `/admin/transfers/${id}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<TransferSchema>(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching transfer:', error);
    throw error;
  }
};

// Submit offline payout
export const submitOfflinePayout = async (id: string, data: OfflinePayoutRequest): Promise<void> => {
  try {
    await api.post(`/admin/transfers/${id}/offline-payouts`, data);
  } catch (error) {
    console.error('Error submitting offline payout:', error);
    throw error;
  }
};

// Get manual payouts (transfers requiring offline payout)
export const getManualPayouts = async (params?: Omit<GetTransfersParams, 'status'>): Promise<GetTransfersResponse> => {
  return getTransfers({
    ...params,
    status: 'requires-offline-payout'
  });
};
